import { TextField } from '@mui/material';
import { useField } from 'formik';
import React from 'react';

// Formik-bound MUI TextField with consistent error display.  Wraps
// `useField` so callers don't have to thread `<Field>` props
// manually.
//
// On multiline fields, anchor the textarea to the top of the input
// container so typed text lays out from line 1 down instead of
// vertically centered.  Two layers cause the centering: (1) the
// InputBase root has `align-items: center` by default in MUI 6 —
// flip to flex-start; (2) the filled variant pads the root rather
// than the textarea, so MUI's hiddenLabel-multiline default of
// 16/17 top/bottom centers a short string in a tall slot.
//
// Padding override is conditional on hiddenLabel: when the label is
// shown, MUI's 25/8 top/bottom is what keeps the floating label from
// crashing into the typed text on focus, so leave that alone and just
// zero the textarea's own padding.  When hiddenLabel is set, no label
// to clear → tighten root to 8/8/12/12 to match single-line filled.
//
// `!important` is necessary on the root padding because MUI's default
// rule is on the root with the same class-chain specificity; emotion's
// source-order otherwise leaves them tied and MUI wins by virtue of
// being declared first.
const baseMultilineSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

const hiddenLabelMultilineSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    paddingTop: '8px !important',
    paddingBottom: '8px !important',
    paddingLeft: '12px !important',
    paddingRight: '12px !important',
  },
};

export default function SettingsTextField({ name, label, helperText, multiline, hiddenLabel, sx, ...rest }) {
  const [field, meta] = useField(name);
  const showError = Boolean(meta.touched && meta.error);
  return (
    <TextField
      {...field}
      {...rest}
      multiline={multiline}
      hiddenLabel={hiddenLabel}
      variant="filled"
      label={label}
      error={showError}
      helperText={showError ? meta.error : helperText}
      fullWidth
      // Coerce null to '' so MUI doesn't warn about uncontrolled→controlled.
      value={field.value ?? ''}
      sx={[multiline && baseMultilineSx, multiline && hiddenLabel && hiddenLabelMultilineSx, sx].filter(Boolean)}
    />
  );
}
